import clsx from "clsx";
import { FC, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { Asset } from "rujira.js";
import {
  AssetLabel,
  IconDenom,
  Icons,
  TableTooltipLabel,
  useTranslation,
  useWindowDimensions,
} from "rujira.ui";
import { useIsBelow } from "../../../hooks/useBreakpoint";
import { assetToTradeUrlSegment } from "../../../services/assetUrl";
import {
  applyTradeUrlState,
  removeDeprecatedTradeParams,
  searchFromParams,
} from "../../tradeUrlState";
import type { TradeLinkState, TradeUrlState } from "../../tradeUrlState";
import { TradeBreakpoint } from "../../static";
import { TabsOverflowContext } from "./Context";
const OrdersBreakpoint = 1350;

export const Nav = <T extends string>({
  tab,
  setTab,
  tabs,
}: {
  tab: T;
  setTab: (v: T) => void;
  tabs: { value: T; count: number; countFirst?: boolean }[];
}) => {
  const rows = useIsBelow(TradeBreakpoint);
  const isCondensed = useWindowDimensions(OrdersBreakpoint) && !rows;
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    open: t("open"),
    filled: t("filled"),
    active: t("active"),
    paused: t("paused"),
    completed: t("completed"),
  };
  const tabsOverflow = useContext(TabsOverflowContext);
  const count = tabs.find((a) => a.value === tab)?.count || 0;

  return isCondensed ? (
    <a className="flex jc-sb">
      <p className="color-white nowrap flex ai-c">
        {labels[tab] ?? tab}
        {count ? <i>{count}</i> : null}
      </p>
      <Icons.AngleDown className="color-white" />
      <nav>
        {tabs.map(({ value, count }) => (
          <span
            key={value}
            className="flex ai-c"
            style={{
              fontSize: "0.75rem",
            }}
            onClick={() => {
              setTab(value);
            }}>
            <label className="ml-1">{labels[value] ?? value}</label>
            {count ? <i>{count || undefined}</i> : null}
          </span>
        ))}
      </nav>
    </a>
  ) : (
    tabs.map(({ value, count, countFirst }) => {
      // Used to set the count alignment on the Completed tab to the left, only when the tabs need to scroll.
      const lead = !!countFirst && tabsOverflow;

      return (
        <a
          key={value}
          className={clsx({
            selected: tab === value,
            "count-first": lead,
          })}
          onClick={() => setTab(value)}>
          {lead && count ? <i>{count}</i> : null}
          {labels[value] ?? value}
          {!lead && count ? <i>{count}</i> : null}
        </a>
      );
    })
  );
};

export const useTradeUrl = (
  assetBase: Asset,
  assetQuote: Asset,
  params?: URLSearchParams,
  state?: TradeUrlState
) => {
  const { search } = useLocation();
  const base = state
    ? applyTradeUrlState(new URLSearchParams(search), state)
    : removeDeprecatedTradeParams(new URLSearchParams(search));
  params?.forEach((value, key) => base.set(key, value));

  return `/trade/${assetToTradeUrlSegment(assetBase)}/${assetToTradeUrlSegment(assetQuote)}${searchFromParams(base)}`;
};

export const PairLink: FC<{ to: string; label: string }> = ({ to, label }) => (
  <Link
    to={to}
    state={{ tab: "graph" } satisfies TradeLinkState}
    className="block color-grey hover-white w-3 h-3"
    data-tooltip-content={label}
    data-tooltip-id="global-tip">
    <Icons.ArrowUpRight className="block w-3 h-3" />
  </Link>
);

export const OrderLink: FC<{
  assetBase: Asset;
  assetQuote: Asset;
  params?: URLSearchParams;
  state?: TradeUrlState;
  tooltip?: string;
}> = ({ assetBase, assetQuote, params, state, tooltip }) => {
  const to = useTradeUrl(assetBase, assetQuote, params, state);

  return (
    <>
      <td className="text-left">
        <Link to={to} relative="path" className="lp small shrink-0">
          <IconDenom denom={assetBase.metadata.symbol} className="w-2" />
          <IconDenom denom={assetQuote.metadata.symbol} className="w-2" />
        </Link>
      </td>
      <td className="text-left">
        <Link
          to={to}
          relative="path"
          className="color-white no-underline hover-teal fw-500 fs-14 nowrap"
          data-tooltip-id={tooltip ? "global-tip" : undefined}
          data-tooltip-html={tooltip ? `<div>${tooltip}</div>` : undefined}>
          <AssetLabel
            asset={assetBase}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />{" "}
          <span className="color-grey">/</span>{" "}
          <AssetLabel
            asset={assetQuote}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />
        </Link>
      </td>
    </>
  );
};

const AnalyticsLabel: FC<{
  label: string;
  tooltip: string;
  underline?: boolean;
}> = ({ label, tooltip, underline }) =>
  underline ? (
    <span
      className="has-tip no-select"
      data-tooltip-id="global-tip"
      data-tooltip-html={tooltip}
      style={{ borderBottom: "1px dotted currentColor" }}>
      {label}
    </span>
  ) : (
    <span
      className="iflex ai-c jc-e"
      data-tooltip-id="global-tip"
      data-tooltip-html={tooltip}>
      {label} <Icons.Info className="w-2 h-2 color-grey block ml-0.5" />
    </span>
  );

const moicTooltip =
  '<div class="w-30">Multiple of Invested Capital (MOIC) measures the total value of your position compared to the amount invested, including both realized and unrealized value.<br/><br/>Example: if you invested $1,000, already received $500 back from the position, and still have $1,500 remaining in the position, your total value is $2,000 and your MOIC is 2.0x.<br/><br/>Calculated as: (Realized Value + Unrealized Value) / Total Invested.</div>';

const dpiTooltip =
  '<div class="w-30">Distribution-to-Paid-In (DPI) ratio measures how much value has been returned compared to the total amount invested.<br/><br/>Example: if you invested $1,000 and received $1,500 back from the position, your DPI is 1.5x and your realized profit is $500.</div>';

const aprTooltip =
  '<div class="w-30">Estimated yearly return based on the claimable yield earned relative to the weighted average value and duration of your position. To measure APR, part of your yield must be set as claimable instead of fully auto-compounded.<br/><br/>Example: if your position earned $100 in claimable yield on an average invested value of $1,000 over one year, the APR would be 10%.</div>';

export const Moic: FC<{ underline?: boolean }> = ({ underline }) => (
  <AnalyticsLabel label="MOIC" tooltip={moicTooltip} underline={underline} />
);

export const Dpi: FC<{ underline?: boolean }> = ({ underline }) => (
  <AnalyticsLabel label="DPI" tooltip={dpiTooltip} underline={underline} />
);

export const Apr: FC<{ underline?: boolean }> = ({ underline }) => (
  <AnalyticsLabel label="APR" tooltip={aprTooltip} underline={underline} />
);

export const CurrentValue = () => (
  <TableTooltipLabel
    label="Current Value"
    tooltip='<div class="w-30">Current value of your Principal, excluding any unclaimed yield.</div>'
  />
);
